import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';
import styled from 'styled-components';

const ChartContainer = styled.div`
  width: 100%;
  height: ${props => props.height || '400px'};
  position: relative;

  svg {
    width: 100%;
    height: 100%;
  }

  .tooltip {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.8);
    color: white;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.2s;
    z-index: 1000;
  }

  .bar {
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.8;
    }
  }

  .axis text {
    font-family: 'Segoe UI', sans-serif;
    font-size: 12px;
  }

  .axis-label {
    font-family: 'Segoe UI', sans-serif;
    font-size: 14px;
    font-weight: 600;
  }
`;

/**
 * D3 Bar Chart Component
 * @param {Array} data - Array of objects with format: [{label: string, value: number, ...}]
 * @param {String} xKey - Key for x-axis labels
 * @param {String} yKey - Key for y-axis values
 * @param {String} xLabel - Label for x-axis
 * @param {String} yLabel - Label for y-axis
 * @param {Function} onClick - Click handler receiving the clicked data item
 * @param {String} color - Bar color (default: LinkedIn Blue)
 * @param {String} height - Chart height (default: 400px)
 * @param {Boolean} horizontal - If true, creates horizontal bars
 */
const D3BarChart = ({ 
  data = [], 
  xKey = 'label', 
  yKey = 'value', 
  xLabel = '', 
  yLabel = '',
  onClick = null,
  color = '#0077B5',
  height = '400px',
  horizontal = false
}) => {
  const svgRef = useRef();
  const tooltipRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 400 });

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width } = containerRef.current.getBoundingClientRect();
        setDimensions({ width, height: parseInt(height) });
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      handleResize();
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [height]);

  useEffect(() => {
    if (!data || data.length === 0 || !svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const margin = { top: 20, right: 30, bottom: horizontal ? 60 : 80, left: horizontal ? 120 : 60 };
    const width = dimensions.width - margin.left - margin.right;
    const chartHeight = dimensions.height - margin.top - margin.bottom;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    if (horizontal) {
      // Horizontal bar chart
      const x = d3.scaleLinear()
        .domain([0, d3.max(data, d => d[yKey])])
        .range([0, width]);

      const y = d3.scaleBand()
        .domain(data.map(d => d[xKey]))
        .range([0, chartHeight])
        .padding(0.2);

      // X Axis
      g.append('g')
        .attr('class', 'axis')
        .attr('transform', `translate(0,${chartHeight})`)
        .call(d3.axisBottom(x).ticks(5));

      // Y Axis
      g.append('g')
        .attr('class', 'axis')
        .call(d3.axisLeft(y));

      // Bars
      g.selectAll('.bar')
        .data(data)
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('x', 0)
        .attr('y', d => y(d[xKey]))
        .attr('width', 0)
        .attr('height', y.bandwidth())
        .attr('fill', color)
        .on('click', (event, d) => {
          if (onClick) onClick(d);
        })
        .on('mouseover', (event, d) => {
          if (tooltipRef.current) {
            d3.select(tooltipRef.current)
              .style('opacity', 1)
              .html(`<strong>${d[xKey]}</strong><br/>${yLabel || 'Value'}: ${d[yKey].toLocaleString()}`)
              .style('left', `${event.pageX + 10}px`)
              .style('top', `${event.pageY - 20}px`);
          }
        })
        .on('mouseout', () => {
          if (tooltipRef.current) {
            d3.select(tooltipRef.current).style('opacity', 0);
          }
        })
        .transition()
        .duration(800)
        .attr('width', d => x(d[yKey]));

      // X Axis Label
      if (yLabel) {
        g.append('text')
          .attr('class', 'axis-label')
          .attr('text-anchor', 'middle')
          .attr('x', width / 2)
          .attr('y', chartHeight + margin.bottom - 10)
          .text(yLabel);
      }

      // Y Axis Label
      if (xLabel) {
        g.append('text')
          .attr('class', 'axis-label')
          .attr('text-anchor', 'middle')
          .attr('transform', 'rotate(-90)')
          .attr('x', -chartHeight / 2)
          .attr('y', -margin.left + 15)
          .text(xLabel);
      }
    } else {
      // Vertical bar chart
      const x = d3.scaleBand()
        .domain(data.map(d => d[xKey]))
        .range([0, width])
        .padding(0.2);

      const y = d3.scaleLinear()
        .domain([0, d3.max(data, d => d[yKey])])
        .range([chartHeight, 0]);

      // X Axis
      g.append('g')
        .attr('class', 'axis')
        .attr('transform', `translate(0,${chartHeight})`)
        .call(d3.axisBottom(x))
        .selectAll('text')
        .attr('transform', 'rotate(-45)')
        .style('text-anchor', 'end');

      // Y Axis
      g.append('g')
        .attr('class', 'axis')
        .call(d3.axisLeft(y).ticks(5));

      // Bars
      g.selectAll('.bar')
        .data(data)
        .enter()
        .append('rect')
        .attr('class', 'bar')
        .attr('x', d => x(d[xKey]))
        .attr('y', chartHeight)
        .attr('width', x.bandwidth())
        .attr('height', 0)
        .attr('fill', color)
        .on('click', (event, d) => {
          if (onClick) onClick(d);
        })
        .on('mouseover', (event, d) => {
          if (tooltipRef.current) {
            d3.select(tooltipRef.current)
              .style('opacity', 1)
              .html(`<strong>${d[xKey]}</strong><br/>${yLabel || 'Value'}: ${d[yKey].toLocaleString()}`)
              .style('left', `${event.pageX + 10}px`)
              .style('top', `${event.pageY - 20}px`);
          }
        })
        .on('mouseout', () => {
          if (tooltipRef.current) {
            d3.select(tooltipRef.current).style('opacity', 0);
          }
        })
        .transition()
        .duration(800)
        .attr('y', d => y(d[yKey]))
        .attr('height', d => chartHeight - y(d[yKey]));

      // X Axis Label
      if (xLabel) {
        g.append('text')
          .attr('class', 'axis-label')
          .attr('text-anchor', 'middle')
          .attr('x', width / 2)
          .attr('y', chartHeight + margin.bottom - 10)
          .text(xLabel);
      }

      // Y Axis Label
      if (yLabel) {
        g.append('text')
          .attr('class', 'axis-label')
          .attr('text-anchor', 'middle')
          .attr('transform', 'rotate(-90)')
          .attr('x', -chartHeight / 2)
          .attr('y', -margin.left + 15)
          .text(yLabel);
      }
    }
  }, [data, dimensions, xKey, yKey, xLabel, yLabel, onClick, color, horizontal]);

  return (
    <ChartContainer ref={containerRef} height={height}>
      <svg ref={svgRef}></svg>
      <div ref={tooltipRef} className="tooltip"></div>
    </ChartContainer>
  );
};

export default D3BarChart;


